<template>
  <div class="s-trips mb-12">
    <TripCard
      v-for="trip in trips"
      :key="trip.bookingId"
      :trip="trip"
      :isLoading="isLoading"
      :appConfig="appConfig"
      @onCancelled="onCancelled"
    />
    <VSkeletonLoader
      v-for="item in 5"
      v-show="isLoading"
      :key="item"
      class="trip-skeleton mx-auto my-8"
      :color="`grey darken-2`"
      type="table-heading, divider, list-item-avatar-three-line"
    />
    <NoTrips v-if="isShowNoTrips" />
    <div v-if="isLoadingMore" class="load-more-container mt-15 mb-10">
      <SSpinner />
    </div>
    <div v-intersect="loadMore" />
  </div>
</template>

<script lang="ts">
export default {
  name: 'Trips',
};
</script>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import SearchRequest from '@slg/web-customer-shared/src/models/search/SearchRequest';
import NumericFilter from '@slg/web-customer-shared/src/models/search/NumericFilter';
import StringFilter from '@slg/web-customer-shared/src/models/search/StringFilter';
import BookingStatusEnum from '@slg/web-customer-shared/src/enum/BookingStatusEnum';
import BookingsService from '@slg/web-customer-shared/src/services/BookingsService';
import OpsZoneService from '@slg/web-customer-shared/src/services/OpsZoneService';
import SSpinner from '@slg/web-customer-shared/src/components/common/SSpinner';
import RideTypeEnum from '@slg/web-customer-shared/src/enum/RideTypeEnum';
import TripCard from '@slg/web-customer-shared/src/components/common/tripCard/TripCard.vue';
import { useGlobalErrorDialog, useVm } from '../../composables';
import NoTrips from './NoTrips.vue';

const props = defineProps({
  currentTabName: {
    type: String,
    required: true,
  },
  appConfig: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update-trips-count']);

const { setGlobalError } = useGlobalErrorDialog();
const vm = useVm();

const isLoading = ref(false);
const isLoadingMore = ref(false);
const pagination = reactive({
  limit: 10,
  loadedCount: 0,
  totalCount: 0,
});
const trips = ref([]);
const opsZones = ref([]);

const isShowNoTrips = computed(() => !trips.value.length && !isLoading.value);

watch(() => props.currentTabName, async () => {
  await getTrips();
  await getOpsZones();
  await getBookingsCount();
}, { immediate: true });

async function loadMore(_observerEntry, _observer, isVisible) {
  if (
    isVisible &&
    !isLoading.value &&
    !isLoadingMore.value &&
    pagination.loadedCount !== pagination.totalCount
  ) {
    await getTrips(pagination.loadedCount);
    await getOpsZones();
  }
}

function onCancelled(cancelledTrip) {
  trips.value = trips.value.filter(
    ({ bookingId }) => cancelledTrip?.bookingId !== bookingId
  );
  pagination.loadedCount -= 1;
  pagination.totalCount -= 1;
  getBookingsCount();
}

function buildSearchRequestForBoardedStatus(offset) {
  const searchRequest = new SearchRequest(
    'estimatedPickUpTime',
    false,
    pagination.limit,
    offset
  );
  searchRequest.addStrFilters([
    new StringFilter('bookingStatus', [BookingStatusEnum.BOARDED], true),
  ]);
  return searchRequest;
}

function buildSearchRequest(offset) {
  const searchRequest = new SearchRequest(
    'estimatedPickUpTime',
    false,
    pagination.limit,
    offset
  );
  switch (props.currentTabName) {
    case 'Planned':
      searchRequest.addNumFilters([
        new NumericFilter('estimatedDropOffTime', Date.now(), 'GTE'),
      ]);
      searchRequest.addStrFilters([
        new StringFilter(
          'bookingStatus',
          Object.values(BookingStatusEnum).filter(
            (status) =>
              status !== BookingStatusEnum.CANCELLED &&
              status !== BookingStatusEnum.BOARDED
          ),
          true
        ),
      ]);
      break;
    case 'Past':
      searchRequest.descending = true; // descending order (from the current day to past)
      searchRequest.addNumFilters([
        new NumericFilter('estimatedDropOffTime', Date.now(), 'LT'),
      ]);
      searchRequest.addStrFilters([
        new StringFilter(
          'bookingStatus',
          Object.values(BookingStatusEnum).filter(
            (status) =>
              status !== BookingStatusEnum.CANCELLED &&
              status !== BookingStatusEnum.BOARDED
          ),
          true
        ),
      ]);
      break;
    case 'Canceled':
      searchRequest.descending = true; // descending order (from the current day to past)
      searchRequest.addStrFilters([
        new StringFilter(
          'bookingStatus',
          [BookingStatusEnum.CANCELLED],
          true
        ),
      ]);
      break;
    default:
      break;
  }

  return searchRequest;
}

function populateReturnTrips() {
  if (props.currentTabName !== 'Planned') {
    return;
  }

  for (let index = 0; index < trips.value?.length; index++) {
    const currentTrip = trips.value[index];

    if (currentTrip.round) {
      //find outward trip from round trip pair by the same order ID
      const outwardTrip = trips.value.find(
        (trip) => trip.orderId === currentTrip.orderId
      );

      if (outwardTrip) {
        outwardTrip.returnTripDateTime = currentTrip.estimatedPickUpTime;
      }
    }
  }
}

function buildOpsZoneSearchRequest(offset, opsZoneIds) {
  const searchRequest = new SearchRequest(
    '',
    false,
    pagination.limit,
    offset
  );
  searchRequest.addStrFilters([
    new StringFilter('opsZoneId', opsZoneIds, true),
  ]);
  return searchRequest;
}

function addOpsZoneOptionsToTrips() {
  const tripsWithOpsZoneOptions = trips.value.map((trip) => {
    const opsZoneOfTrip = opsZones.value.find(
      (opsZone) => trip.opsZoneId === opsZone.opsZoneId
    );
    return {
      ...trip,
      haveShuttleTypeOptions:
        !props.appConfig.isNightCardsSupport &&
        opsZoneOfTrip.shuttleType === RideTypeEnum.BOTH,
      haveVehicleOptions: opsZoneOfTrip.vehicleCategories.length > 1,
      timeZone: opsZoneOfTrip.timeZone,
    };
  });
  trips.value = tripsWithOpsZoneOptions;
}

async function getOpsZones(offset = 0) {
  const opsZoneIds = [...new Set(trips.value.map((trip) => trip.opsZoneId))];
  const searchRequest = buildOpsZoneSearchRequest(offset, opsZoneIds);
  const { data } = await OpsZoneService.opsZoneSearch(searchRequest);
  opsZones.value = data.opsZones;
  addOpsZoneOptionsToTrips();
}

async function getBookingsCount() {
  let tripsCount = {
    cancelled: null,
    past: null,
    planned: null,
  };
  try {
    const { data } = await BookingsService.getBookingsCount();
    tripsCount = data;
  } catch (error) {
    if (error.status !== 401) {
      await setGlobalError(error);
    }
  }
  emit('update-trips-count', tripsCount);
}

async function getTrips(offset = 0) {
  if (offset) {
    isLoadingMore.value = true;
  } else {
    isLoading.value = true;
  }
  try {
    const searchRequest = buildSearchRequest(offset);
    const {
      data: { bookings, totalCount },
    } = await BookingsService.bookingsSearch(searchRequest);

    const bookingsByDefaultSearching = bookings;
    const totalCountByDefaultSearching = totalCount;

    let bookingsWithoutTimeImpact = [];

    if (props.currentTabName === 'Planned') {
      const searchRequestForBoardedStatus =
        buildSearchRequestForBoardedStatus(offset);
      const {
        data: { bookings },
      } = await BookingsService.bookingsSearch(
        searchRequestForBoardedStatus
      );
      bookingsWithoutTimeImpact = bookings;
    }

    if (offset) {
      trips.value =
        props.currentTabName === 'Planned'
          ? [
              ...trips.value,
              ...bookingsByDefaultSearching,
              ...bookingsWithoutTimeImpact,
            ].sort((a, b) => a.estimatedPickUpTime - b.estimatedPickUpTime)
          : [
              ...trips.value,
              ...bookingsByDefaultSearching,
              ...bookingsWithoutTimeImpact,
            ];
      pagination.loadedCount += bookingsByDefaultSearching.length;
    } else {
      pagination.loadedCount = bookingsByDefaultSearching.length;
      trips.value =
        props.currentTabName === 'Planned'
          ? [
              ...bookingsByDefaultSearching,
              ...bookingsWithoutTimeImpact,
            ].sort((a, b) => a.estimatedPickUpTime - b.estimatedPickUpTime)
          : [...bookingsByDefaultSearching, ...bookingsWithoutTimeImpact];
    }
    pagination.totalCount = totalCountByDefaultSearching;
    populateReturnTrips();
  } catch (error) {
    trips.value = [];
    vm.totalCount = {
      planned: null,
      past: null,
      cancelled: null,
    };
    if (error.status !== 401) {
      await setGlobalError(error);
    }
  }
  isLoading.value = false;
  isLoadingMore.value = false;
}
</script>

<style lang="scss" scoped>
.s-trips {
  max-width: 90%;
  min-width: 320px;
}

.trip-skeleton {
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
}

.load-more-container {
  z-index: 0;
  position: relative;
}
</style>
